/* eslint-disable @typescript-eslint/no-explicit-any */
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import SntUnGroup from "@icons/SntUnGroup";
import { getConfigColumnStyle, headerProps } from "../../utils/tableUtils";
import { ConfigButton, SortIcon, ColumnMenu } from "../shared";

interface StickyTableHeaderProps {
  headerGroups: any[];
  dataFilter: any;
  rows: any[];
  prepareRow: (row: any) => void;
  toggleSortBy: (columnKey: string, desc: boolean) => void;
  // Config
  isHideConfigColumn: boolean;
  onConfigColumn?: () => void;
  onRenderConfig?: (original: any) => React.ReactNode;
  // Group
  isDisabbledGroup: boolean;
  // Copy/Export
  isDisabledCopyData: boolean;
  onExtraColumnActionClicked?: (action: string, params: any) => void;
  // Filter
  isDisabbledAddFilter: boolean;
  onFilterClicked?: (
    action: string,
    columnKey: string,
    filterKeys: string[],
    cellValue: any[]
  ) => void;
  restrictedFilters?: string[];
  filterVisibleKeys?: string[];
  // User info
  organisationType?: string;
}

export const StickyTableHeader = ({
  headerGroups,
  dataFilter,
  rows,
  prepareRow,
  toggleSortBy,
  isHideConfigColumn,
  onConfigColumn,
  onRenderConfig,
  isDisabbledGroup,
  isDisabledCopyData,
  onExtraColumnActionClicked,
  isDisabbledAddFilter,
  onFilterClicked,
  restrictedFilters,
  filterVisibleKeys,
  organisationType,
}: StickyTableHeaderProps) => {
  const showConfigColumn =
    (!isHideConfigColumn && onConfigColumn) || onRenderConfig;

  return (
    <div style={{ backgroundColor: "white", padding: "0px", zIndex: 99 }}>
      {headerGroups.map((headerGroup: any, idx: number) => {
        const { key: groupKey, ...groupProps } =
          headerGroup.getHeaderGroupProps();
        return (
          <div key={groupKey ?? idx} {...groupProps} className="tr mh-30">
            {showConfigColumn && (
              <div
                className="th align-items-center"
                style={getConfigColumnStyle()}
              >
                {!isHideConfigColumn && onConfigColumn && (
                  <ConfigButton onConfigColumn={onConfigColumn} />
                )}
              </div>
            )}

            {headerGroup.headers.map((column: any, index: number) => {
              const { key: headerKey, ...headerPropsRest } =
                column.getHeaderProps(headerProps);
              return (
                <div
                  key={headerKey ?? index}
                  {...headerPropsRest}
                  className="th align-items-center justify-content-between"
                >
                  <div className="d-flex align-items-center">
                    <div
                      {...column.getSortByToggleProps({
                        title: column.description,
                      })}
                      onClick={() => {
                        let desc = false;
                        if (column.isSorted) {
                          desc = !column.isSortedDesc;
                        }
                        if (!column.disableSortBy) {
                          toggleSortBy(column.columnKey, desc);
                        }
                      }}
                    >
                      {column.isGrouped && (
                        <span style={{ fontWeight: "normal" }}>
                          {translate(LanguageKey.group_by_key)}:{" "}
                        </span>
                      )}
                      {column.render("Header")}
                    </div>

                    <SortIcon column={column} toggleSortBy={toggleSortBy} />

                    {column.isGrouped && (
                      <div className="ms-2" style={{ cursor: "pointer" }}>
                        <span
                          className="group-header-icon"
                          title={translate(LanguageKey.ungroup_column_key)}
                          onClick={() => {
                            dataFilter?.toggleGroupColumnKey?.(column.key);
                          }}
                        >
                          <SntUnGroup />
                        </span>
                      </div>
                    )}
                  </div>

                  <ColumnMenu
                    column={column}
                    headerGroup={headerGroup}
                    dataFilter={dataFilter}
                    rows={rows}
                    prepareRow={prepareRow}
                    toggleSortBy={toggleSortBy}
                    isDisabbledGroup={isDisabbledGroup}
                    isDisabledCopyData={isDisabledCopyData}
                    isDisabbledAddFilter={isDisabbledAddFilter}
                    onConfigColumn={onConfigColumn}
                    onExtraColumnActionClicked={onExtraColumnActionClicked}
                    onFilterClicked={onFilterClicked}
                    restrictedFilters={restrictedFilters}
                    filterVisibleKeys={filterVisibleKeys}
                    organisationType={organisationType}
                  />

                  <div
                    {...column.getResizerProps()}
                    className={`resizer ${column.isResizing ? "isResizing" : ""}`}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
